import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton, Tooltip, LinearProgress } from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { getSectionBackgroundColor, getSectionIconColor } from '../../../utils/sectionColors';

/**
 * Task tile component for displaying a single build task
 * @param {Object} props - Component props
 * @param {Object} props.task - Task data object containing task details
 * @param {Function} props.onMenuOpen - Function to open the menu
 * @param {Function} props.onClick - Function to handle click on the tile
 * @returns {React.ReactElement} Task tile component
 */
const TaskTile = ({ task, onMenuOpen, onClick }) => {
  // Calculate color based on phase and task status
  const getTaskColor = () => {
    // First check task status
    if (task.status === 'completed') {
      return '#e8f5e9'; // Light green for completed tasks
    }
    if (task.status === 'in_progress') {
      return '#e3f2fd'; // Light blue for in-progress tasks
    }
    if (task.status === 'blocked') {
      return '#ffebee'; // Light red for blocked tasks
    }
    
    // If no specific status, use phase-based colors
    const phase = task.phase?.toLowerCase() || '';
    
    if (phase.includes('setup') || phase.includes('infrastructure')) {
      return '#e3f2fd'; // Light blue for setup/infrastructure tasks
    }
    if (phase.includes('core') || phase.includes('foundation')) {
      return '#e0f7fa'; // Light cyan for core components
    }
    if (phase.includes('feature') || phase.includes('implementation')) {
      return '#fff3e0'; // Light orange for feature implementation
    }
    if (phase.includes('test') || phase.includes('quality')) {
      return '#f3e5f5'; // Light purple for testing tasks
    }
    if (phase.includes('deploy') || phase.includes('release')) {
      return '#e8f5e9'; // Light green for deployment tasks
    }
    
    // Default color
    return '#f5f5f5'; // Light gray for other tasks
  };
  
  // Calculate icon color based on phase and task status
  const getIconColor = () => {
    // First check task status
    if (task.status === 'completed') {
      return '#66bb6a'; // Green for completed tasks
    }
    if (task.status === 'in_progress') {
      return '#42a5f5'; // Blue for in-progress tasks
    }
    if (task.status === 'blocked') {
      return '#f44336'; // Red for blocked tasks
    }
    
    // If no specific status, use phase-based colors
    const phase = task.phase?.toLowerCase() || '';
    
    if (phase.includes('setup') || phase.includes('infrastructure')) {
      return '#42a5f5'; // Blue for setup/infrastructure tasks
    }
    if (phase.includes('core') || phase.includes('foundation')) {
      return '#26c6da'; // Cyan for core components
    }
    if (phase.includes('feature') || phase.includes('implementation')) {
      return '#ff9800'; // Orange for feature implementation
    }
    if (phase.includes('test') || phase.includes('quality')) {
      return '#ab47bc'; // Purple for testing tasks
    }
    if (phase.includes('deploy') || phase.includes('release')) {
      return '#66bb6a'; // Green for deployment tasks
    }
    
    // Default color
    return '#475969'; // Slate for other tasks
  };

  // Calculate progress percentage if available
  const progressPercentage = task.progress || (task.status === 'completed' ? 100 : 0);
  
  return (
    <Box 
      sx={{ 
        height: 160, // Increased height to accommodate progress bar
        width: '100%',
        maxWidth: { xs: '180px', md: '170px', lg: '160px', xl: '160px' },
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: 0,
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: getTaskColor(),
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        ml: 'auto',
        mr: 'auto',
        mb: 1,
        border: 'none',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 4px 8px rgba(0,0,0,0.05)'
        }
      }}
      onClick={() => onClick(task)}
    >
      {/* Status indicator */}
      {task.status === 'completed' && (
        <Box 
          sx={{ 
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 2
          }}
        >
          <svg id="Tick" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 28.027 28.027">
            <path id="Path_58116" data-name="Path 58116" d="M28.027,14.014l-.943,1.722.469,1.906L26.2,19.059l-.044,1.963-1.68,1.017-.55,1.885-1.884.55-1.017,1.679-1.963.044-1.418,1.358-1.906-.469-1.722.943-1.721-.943-1.906.469L8.968,26.2l-1.963-.044-1.017-1.68L4.1,23.923l-.55-1.884-1.68-1.017-.044-1.963L.474,17.641l.469-1.906L0,14.014l.943-1.721L.474,10.386,1.832,8.968l.044-1.963,1.68-1.017L4.1,4.1l1.885-.55,1.016-1.68,1.963-.044L10.386.474l1.906.469L14.014,0l1.722.943L17.641.474l1.418,1.358,1.963.044,1.017,1.68,1.885.55.55,1.885,1.679,1.016L26.2,8.968l1.358,1.418-.469,1.906Z" fill="#62c148"></path>
            <path id="Path_58117" data-name="Path 58117" d="M289.508,319.711l-3.968-3.968,1.318-1.318,2.52,2.52,7.586-9.166,1.436,1.189Z" transform="translate(-277.955 -299.731)" fill="#fff"></path>
          </svg>
        </Box>
      )}
      
      {/* In-progress indicator */}
      {task.status === 'in_progress' && (
        <Box 
          sx={{ 
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 2,
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: '#42a5f5',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="white">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </Box>
      )}
      
      {/* Blocked indicator */}
      {task.status === 'blocked' && (
        <Tooltip title={task.blockReason || 'Task is blocked'}>
          <Box 
            sx={{ 
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 2,
              width: 24,
              height: 24,
              borderRadius: '50%',
              backgroundColor: '#f44336',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="white">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Box>
        </Tooltip>
      )}
      
      {/* Menu button at bottom right */}
      <Tooltip title="View actions">
        <IconButton
          sx={{
            position: 'absolute',
            bottom: 2,
            right: 2,
            zIndex: 2,
            opacity: 0.7,
            color: '#475969',
            padding: '4px',
            minWidth: '28px',
            minHeight: '28px',
            '&:hover': {
              opacity: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.05)'
            },
          }}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onMenuOpen(e, task);
          }}
        >
          <MoreVertIcon style={{ fontSize: '18px' }} />
        </IconButton>
      </Tooltip>
      
      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          pt: 4,
          pb: 3.5,
          px: 1.5,
          textAlign: 'center'
        }}
      >
        <Box 
          sx={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 2,
            height: '60px',
            width: '60px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.6)',
            color: getIconColor()
          }}
        >
          {React.cloneElement(task.icon, { 
            style: { 
              fontSize: '2.2rem',
              color: 'inherit' 
            }
          })}
        </Box>
        
        <Typography 
          variant="body1" 
          component="h3" 
          sx={{ 
            fontSize: '0.9rem',
            fontWeight: 500,
            color: '#475969',
            lineHeight: 1.3,
            mt: 1,
            // Limit to 2 lines with ellipsis
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {task.title}
        </Typography>
        
        {/* Phase label */}
        {task.phase && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'text.secondary',
              mt: 0.5,
              fontSize: '0.7rem',
              fontStyle: 'italic',
            }}
          >
            {task.phase}
          </Typography>
        )}
        
        {/* Progress indicator */}
        {task.status === 'in_progress' && (
          <Box sx={{ width: '80%', mt: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={progressPercentage} 
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getIconColor(),
                }
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

TaskTile.propTypes = {
  task: PropTypes.object.isRequired,
  onMenuOpen: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired
};

export default React.memo(TaskTile);